import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type MediaType = 'image' | 'group-image' | 'video';

export interface UploadItem {
    id: string;
    fileName: string;
    fileSize: number;
    progress: number;
    /**
     * `saving` é a etapa depois do upload: o arquivo já está no storage, mas ainda
     * está sendo gravado no documento. Sem ela a pílula sumia nesse ponto e o
     * usuário ficava num vão mudo até a foto aparecer no card.
     */
    status: 'compressing' | 'uploading' | 'saving' | 'completed' | 'error';
    mediaType?: MediaType;
    error?: string;
}

@Injectable({
    providedIn: 'root',
})
export class UploadProgressService {
    private _uploads$ = new BehaviorSubject<UploadItem[]>([]);

    /**
     * Observable dos uploads em andamento
     */
    public get uploads$(): Observable<UploadItem[]> {
        return this._uploads$.asObservable();
    }

    /**
     * Retorna a lista atual de uploads
     */
    public get uploads(): UploadItem[] {
        return this._uploads$.value;
    }

    /**
     * Adiciona um novo upload à lista
     */
    public addUpload(id: string, fileName: string, fileSize: number, mediaType?: MediaType): void {
        const currentUploads = this._uploads$.value;
        const newUpload: UploadItem = {
            id,
            fileName,
            fileSize,
            progress: 0,
            status: 'uploading',
            mediaType,
        };

        this._uploads$.next([...currentUploads, newUpload]);
    }

    /**
     * Adiciona um upload que está sendo comprimido
     */
    public addCompressing(
        id: string,
        fileName: string,
        fileSize: number,
        mediaType?: MediaType
    ): void {
        const currentUploads = this._uploads$.value;
        const newUpload: UploadItem = {
            id,
            fileName,
            fileSize,
            progress: 0,
            status: 'compressing',
            mediaType,
        };

        this._uploads$.next([...currentUploads, newUpload]);

        // Progresso real será atualizado via updateCompressionProgress
        // Não precisa mais simular
    }

    /**
     * Limpa todos os uploads ativos
     */
    clearUploads(): void {
        const currentUploads = this._uploads$.value;
        const activeUploads = currentUploads.filter(upload => upload.status !== 'completed');
        this._uploads$.next(activeUploads);
    }

    /**
     * Muda o status de compressing para uploading
     */
    public startUploading(id: string): void {
        const currentUploads = this._uploads$.value;
        const updatedUploads: UploadItem[] = currentUploads.map(upload =>
            upload.id === id ? { ...upload, status: 'uploading' as const, progress: 0 } : upload
        );

        this._uploads$.next(updatedUploads);
    }

    /**
     * Atualiza o progresso de compressão de um vídeo
     */
    public updateCompressionProgress(id: string, progress: number): void {
        const currentUploads = this._uploads$.value;
        const upload = currentUploads.find(u => u.id === id);

        if (upload?.status === 'compressing') {
            const updatedUploads = currentUploads.map(upload =>
                upload.id === id
                    ? { ...upload, progress: Math.min(Math.max(Math.round(progress), 0), 95) }
                    : upload
            );
            this._uploads$.next(updatedUploads);
        }
    }

    /**
     * Atualiza o progresso de um upload
     */
    public updateProgress(id: string, progress: number): void {
        const currentUploads = this._uploads$.value;
        const updatedUploads = currentUploads.map(upload =>
            upload.id === id ? { ...upload, progress: Math.min(progress, 100) } : upload
        );

        this._uploads$.next(updatedUploads);
    }

    /**
     * Upload concluído, gravação no documento em andamento.
     *
     * Mantém a pílula viva (com progresso cheio e rótulo "Salvando") até
     * `completeUpload`: entre o storage e o documento havia um silêncio em que a
     * tela não mostrava nem progresso nem a mídia.
     */
    public startSaving(id: string): void {
        const currentUploads = this._uploads$.value;
        const updatedUploads: UploadItem[] = currentUploads.map(upload =>
            upload.id === id ? { ...upload, status: 'saving' as const, progress: 100 } : upload
        );

        this._uploads$.next(updatedUploads);
    }

    /**
     * Marca um upload como completo
     */
    public completeUpload(id: string): void {
        // Remove imediatamente quando completo (sem mostrar mensagem)
        this.removeUpload(id);
    }

    /**
     * Marca um upload como erro
     */
    public errorUpload(id: string, error: string): void {
        const currentUploads = this._uploads$.value;
        const updatedUploads: UploadItem[] = currentUploads.map(upload =>
            upload.id === id ? { ...upload, status: 'error' as const, error } : upload
        );

        this._uploads$.next(updatedUploads);

        // Remove após 5 segundos
        setTimeout(() => this.removeUpload(id), 5000);
    }

    /**
     * Remove um upload da lista
     */
    public removeUpload(id: string): void {
        const currentUploads = this._uploads$.value;
        const filteredUploads = currentUploads.filter(upload => upload.id !== id);
        this._uploads$.next(filteredUploads);
    }

    /**
     * Limpa todos os uploads completos
     */
    public clearCompleted(): void {
        const currentUploads = this._uploads$.value;
        const activeUploads = currentUploads.filter(upload => upload.status !== 'completed');
        this._uploads$.next(activeUploads);
    }
}
